import {
  agentInvoiceOf,
  asicInvoiceOf,
  isPayable,
  isUnpaid,
  needsMySignature,
} from './outstanding';
import { DOCUMENT_TYPE, type OutstandingItem } from './outstanding.schema';

/**
 * Every action a row can offer, derived in one place.
 *
 * The legacy screen spread eleven visibility conditions across eleven JSX
 * guards inside a 1,004-line file, all of them behind a single "Actions" caret.
 * Two things change here: the conditions become one table that can be tested
 * without rendering, and the action a row most needs is promoted onto the row
 * surface so a director can see what a row wants without opening a menu.
 */

/** `pay` and `sign` open a dialog; `link` navigates to a file. */
export type OutstandingActionKind =
  | 'pay'
  | 'sign'
  | 'invite'
  | 'viewDocument'
  | 'viewAsicInvoice'
  | 'viewAgentInvoice'
  | 'viewReceipt'
  | 'viewResolution';

export interface OutstandingAction {
  kind: OutstandingActionKind;
  label: string;
  /** Present on link actions. Absent means the action opens a dialog. */
  href?: string;
  /** Kept in the list rather than filtered out: the legacy menu showed
   *  "File Not Available" as a disabled item, and silently dropping the row
   *  instead would leave a director wondering where their form went. */
  disabled?: boolean;
}

const linkAction = (
  kind: OutstandingActionKind,
  label: string,
  href: string,
): OutstandingAction =>
  href === '' ? { kind, label: 'File not available', disabled: true } : { kind, label, href };

/**
 * Which payment route a row takes.
 *
 * Three distinct charges hide behind one "Pay" label: an annual review settles
 * two legs (agent + ASIC), a form settles its attached invoice, and a
 * standalone ASIC invoice settles itself. They hit different endpoints, so the
 * distinction has to survive into the dialog.
 */
export type PaymentRoute = 'annualReview' | 'formInvoice' | 'asicInvoice';

export const paymentRouteOf = (item: OutstandingItem): PaymentRoute | undefined => {
  if (!isUnpaid(item) || !isPayable(item)) return undefined;
  if (item.annualReviewId !== '') return 'annualReview';
  if (item.documentType === DOCUMENT_TYPE.form) {
    return item.invoices.length > 0 ? 'formInvoice' : undefined;
  }
  if (item.documentType === DOCUMENT_TYPE.invoice) return 'asicInvoice';
  return undefined;
};

/**
 * Whether the row can invite co-directors to sign.
 *
 * Preserves the legacy condition — unsigned, and either a form or an annual
 * review invoice. The legacy version additionally called the signers endpoint
 * *before* opening the dialog and showed an error toast when nobody came back;
 * that check now lives in the dialog, which can show the same information
 * without costing a request on a menu that may never be opened.
 */
export const canInviteSigners = (item: OutstandingItem): boolean => {
  if (item.signStatus !== 'unsigned') return false;
  return (
    item.documentType === DOCUMENT_TYPE.form ||
    (item.documentType === DOCUMENT_TYPE.invoice && item.annualReviewId !== '')
  );
};

/**
 * The one action the row most needs, surfaced as a button.
 *
 * Paying outranks signing: an unpaid document is the one with a due date and a
 * late fee behind it. A row that needs neither has no primary action — its
 * documents are listed on the card instead.
 */
export const primaryActionOf = (item: OutstandingItem): OutstandingAction | undefined => {
  if (paymentRouteOf(item) !== undefined) {
    return { kind: 'pay', label: 'Pay' };
  }
  if (needsMySignature(item)) {
    return { kind: 'sign', label: 'Sign' };
  }
  // No obligation left, so no primary action. The row's document used to fill
  // this slot, which made sense while it was the only way to reach the file;
  // `documentsOf` now lists it with its own View and Download, and promoting a
  // copy of it here would put the same document on the card twice.
  return undefined;
};

/** The document's name. A noun, not a command: it heads a row with its own
 *  View and Download buttons rather than being a menu item you activate. */
const documentLabelOf = (item: OutstandingItem): string => {
  if (item.documentType === DOCUMENT_TYPE.dividend) return 'Dividend statement';
  if (item.documentType === DOCUMENT_TYPE.form) return 'Form';
  return 'Document';
};

/** The invoice attachments, as links. Split out because building them is a
 *  self-contained job and inlining it pushed `secondaryActionsOf` past the
 *  complexity limit. */
const invoiceActionsOf = (item: OutstandingItem): OutstandingAction[] => {
  const actions: OutstandingAction[] = [];

  const asic = asicInvoiceOf(item);
  if (asic !== undefined) {
    actions.push(linkAction('viewAsicInvoice', 'ASIC invoice', asic.fileURL));
  }

  const agent = agentInvoiceOf(item);
  if (agent !== undefined) {
    actions.push(linkAction('viewAgentInvoice', 'Agent invoice', agent.fileURL));
  }

  return actions;
};

/** The supporting paperwork: the resolution behind an annual review, and the
 *  receipt for anything already paid. */
const documentActionsOf = (item: OutstandingItem): OutstandingAction[] => {
  const actions: OutstandingAction[] = [];

  const resolution = item.solvency?.fileURL ?? '';
  if (resolution !== '') {
    actions.push({ kind: 'viewResolution', label: 'Resolution', href: resolution });
  }

  if (item.paymentReceiptURL !== '') {
    actions.push({
      kind: 'viewReceipt',
      label: 'Payment receipt',
      href: item.paymentReceiptURL,
    });
  }

  return actions;
};

/**
 * The row's paperwork: the document itself, its invoices, the resolution and
 * any receipt.
 *
 * Split from `secondaryActionsOf` so the menu can tell a document from a
 * command. Sign and Invite are activated; a document instead offers View and
 * Download, which is two actions on one row rather than one menu item. Keeping
 * the two kinds separable here is what lets the menu render each correctly
 * without inspecting labels.
 */
export const documentsOf = (item: OutstandingItem): OutstandingAction[] => {
  const actions: OutstandingAction[] = [];

  const showsDocument =
    item.documentType === DOCUMENT_TYPE.form || item.documentType === DOCUMENT_TYPE.dividend;
  if (showsDocument) {
    actions.push(linkAction('viewDocument', documentLabelOf(item), item.fileURL));
  }

  return [...actions, ...invoiceActionsOf(item), ...documentActionsOf(item)];
};

/**
 * Everything the row offers beyond its primary action.
 *
 * Only the commands. Documents are NOT here: they are listed on the surface
 * that owns them — a change card renders its own documents and invoices, each
 * with its own View and Download — and repeating them in this menu put every
 * file on screen twice under two different names.
 *
 * Signing survives into the menu when paying took the primary slot, because a
 * row that is both unpaid and unsigned needs both routes offered.
 */
export const secondaryActionsOf = (item: OutstandingItem): OutstandingAction[] => {
  const actions: OutstandingAction[] = [];
  const primary = primaryActionOf(item);

  if (needsMySignature(item) && primary?.kind !== 'sign') {
    actions.push({ kind: 'sign', label: 'Sign' });
  }

  if (canInviteSigners(item)) {
    actions.push({ kind: 'invite', label: 'Invite others to sign' });
  }

  return actions;
};
